import { useState } from "react"
import { CalendarIcon } from "lucide-react"
import { Button } from "./button"
import { Calendar } from "./calendar"
import { Popover, PopoverContent, PopoverTrigger } from "./popover"
import { cn } from "../../lib/utils"

/**
 * DatePicker — selector de fecha estándar del sistema (shadcn Popover + Calendar).
 *
 * Reemplaza al `<input type="date">` nativo (que muestra el picker del navegador,
 * inconsistente con el diseño). Trabaja con strings `YYYY-MM-DD` (vacío = sin fecha):
 *
 *   <DatePicker value={fecha} onChange={setFecha} />
 *   <DatePicker value={f.date} onChange={(v) => setForm(prev => ({ ...prev, date: v }))} className="h-8 w-[140px]" />
 */
export function DatePicker({
  value,
  onChange,
  placeholder = "Fecha",
  className,
  id,
  disabled,
  fromYear = 2000,
  toYear,
  minDate,
}: {
  value?: string
  onChange: (date: string) => void
  placeholder?: string
  className?: string
  id?: string
  disabled?: boolean
  fromYear?: number
  toYear?: number
  /** YYYY-MM-DD: los días anteriores no se pueden elegir */
  minDate?: string
}) {
  const [open, setOpen] = useState(false)
  const date = value ? new Date(`${value}T12:00:00`) : undefined
  const minimo = minDate ? new Date(`${minDate}T00:00:00`) : null
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          id={id}
          disabled={disabled}
          variant="outline"
          className={cn("w-full justify-start text-left font-normal px-2.5", !date && "text-muted-foreground", className)}
        >
          <CalendarIcon className="mr-1.5 h-4 w-4 opacity-60 shrink-0" />
          <span className="truncate">
            {date ? date.toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit", year: "numeric" }) : placeholder}
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={date}
          disabled={minimo ? (d: Date) => d < minimo : undefined}
          captionLayout="dropdown"
          fromYear={fromYear}
          toYear={toYear ?? new Date().getFullYear() + 10}
          onSelect={(d: Date | undefined) => {
            if (d) {
              const y = d.getFullYear()
              const m = String(d.getMonth() + 1).padStart(2, "0")
              const dd = String(d.getDate()).padStart(2, "0")
              onChange(`${y}-${m}-${dd}`)
            } else {
              onChange("")
            }
            setOpen(false)
          }}
        />
      </PopoverContent>
    </Popover>
  )
}

export default DatePicker
